'use client'

import { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Search, MapPin, Copy, Check, ArrowLeft, Info, Building2 } from 'lucide-react'
import Footer from '@/components/Footer'

export const runtime = 'edge'

type Cep = {
  logradouro: string
  bairro: string
  cep: string
}

function normalizar(texto: string) {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

export default function CepPage() {
  const [dados, setDados] = useState<Cep[]>([])
  const [busca, setBusca] = useState('')
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)

  useEffect(() => {
    fetch('/ceps-catanduvas.json')
      .then(res => res.json())
      .then(data => setDados(data))
  }, [])

  const resultados = useMemo(() => {
    if (!busca.trim()) {
      return []
    }

    const termos = normalizar(busca).split(' ')

    const filtrados = dados.filter(item => {
      const texto = normalizar(item.logradouro + ' ' + item.bairro)
      return termos.every(t => texto.includes(t))
    })

    return filtrados.slice(0, 30)
  }, [busca, dados])

  const copyToClipboard = (text: string, index: number) => {
    navigator.clipboard.writeText(text)
    setCopiedIndex(index)
    setTimeout(() => setCopiedIndex(null), 2000)
  }

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 selection:bg-blue-200 selection:text-blue-900">
      {/* Header / Hero */}
      <header className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-12 md:py-20 text-center space-y-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 px-6 py-3 rounded-2xl text-white font-black uppercase tracking-tighter italic transition-all hover:-translate-x-1 active:scale-95 mb-8 group shadow-lg"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Voltar para o Início
          </Link>
          <h1 className="text-3xl md:text-5xl font-black tracking-tighter uppercase italic">
            Consulta de CEP <br className="md:hidden" />
            <span className="text-blue-200">Catanduvas PR</span>
          </h1>
          <p className="text-blue-100 text-lg md:text-xl font-medium max-w-2xl mx-auto">
            Encontre o CEP de qualquer rua ou bairro de nossa cidade de forma rápida e fácil.
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 -mt-8 pb-20">
        {/* Search Bar */}
        <div className="relative group">
          <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
            <Search className="h-6 w-6 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
          </div>
          <input
            type="text"
            placeholder="Digite o nome da rua, bairro ou região..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
            className="block w-full pl-14 pr-4 py-5 bg-white border-none rounded-3xl text-lg shadow-xl focus:ring-4 focus:ring-blue-500/20 transition-all outline-none placeholder:text-slate-400"
          />
        </div>

        {/* Results Section */}
        <div className="mt-10 space-y-4">
          {busca === '' ? (
            <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-sm">
              <div className="bg-blue-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Info className="w-10 h-10 text-blue-500" />
              </div>
              <h3 className="text-xl font-bold text-slate-800">Pronto para buscar?</h3>
              <p className="text-slate-500 mt-2">Digite o nome de uma rua ou bairro acima para ver os resultados.</p>
            </div>
          ) : resultados.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-sm">
              <div className="bg-slate-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="w-10 h-10 text-slate-300" />
              </div>
              <h3 className="text-xl font-bold text-slate-800">Nenhum resultado encontrado</h3>
              <p className="text-slate-500 mt-2">Tente buscar por termos mais genéricos ou verifique a ortografia.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between px-4 mb-2">
                <span className="text-sm font-bold text-slate-400 uppercase tracking-widest">
                  {resultados.length} {resultados.length === 1 ? 'resultado encontrado' : 'resultados encontrados'}
                </span>
              </div>
              <div className="grid grid-cols-1 gap-4">
                {resultados.map((item, i) => (
                  <div 
                    key={i} 
                    className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm hover:shadow-md transition-all group flex flex-col md:flex-row md:items-center justify-between gap-4"
                  >
                    <div className="flex items-start gap-4">
                      <div className="bg-blue-50 p-3 rounded-2xl group-hover:bg-blue-600 transition-colors">
                        <MapPin className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors" />
                      </div>
                      <div>
                        <h4 className="text-xl font-black text-slate-800 uppercase tracking-tight leading-tight">
                          {item.logradouro}
                        </h4>
                        <div className="flex items-center gap-2 text-slate-500 font-medium mt-1">
                          <Building2 className="w-4 h-4" />
                          <span>Bairro: {item.bairro}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 md:self-center">
                      <div className="bg-slate-50 px-5 py-3 rounded-2xl border border-slate-100">
                        <span className="text-2xl font-black text-blue-700 tracking-tighter italic">
                          {item.cep}
                        </span>
                      </div>
                      <button
                        onClick={() => copyToClipboard(item.cep, i)}
                        className={`p-3 rounded-2xl transition-all active:scale-90 ${
                          copiedIndex === i 
                            ? 'bg-green-500 text-white' 
                            : 'bg-slate-100 text-slate-600 hover:bg-blue-600 hover:text-white'
                        }`}
                        title="Copiar CEP"
                      >
                        {copiedIndex === i ? <Check className="w-6 h-6" /> : <Copy className="w-6 h-6" />}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer Info */}
        <div className="mt-12 p-8 bg-blue-50 rounded-[2rem] border border-blue-100">
          <div className="flex items-start gap-4">
            <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
            <div className="space-y-2">
              <h4 className="font-bold text-blue-900">Sobre os dados</h4>
              <p className="text-blue-700 text-sm leading-relaxed">
                Os dados de CEP apresentados nesta página são referentes exclusivamente ao município de <strong>Catanduvas - PR</strong>. 
                As informações são atualizadas periodicamente para garantir a precisão nas suas consultas e envios.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}
